import React, { useEffect, useState, useRef } from "react";
import MasterLayoutGames from "../layouts/GamesLayout";
import data from "../data/parittaPuzzle.json";
import { toPinyinSync, capitalizePinyin } from "../utils/pinyinUtil";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ParittaPuzzlePage() {
  const [mode, setMode] = useState("easy");
  const [chunks, setChunks] = useState([]);
  const [shuffled, setShuffled] = useState([]);
  const [answer, setAnswer] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [isWrong, setIsWrong] = useState(false);

  const matchAudioRef = useRef(null);
  const winAudioRef = useRef(null);

	const [parittaIndex, setParittaIndex] = useState(0);
  const getRandomIndex = (max) => Math.floor(Math.random() * max);
	const [itemIndex, setItemIndex] = useState(() => {
    const firstGroup = data[0];
    return getRandomIndex(firstGroup.items.length);
  });

	const currentGroup = data[parittaIndex];
	const current = currentGroup.items[itemIndex];

	const totalItems = currentGroup.items.length;

	const changeRandomItem = () => {
		if (totalItems <= 1) return;

		let newIndex = itemIndex;

		// pastikan tidak sama
		while (newIndex === itemIndex) {
			newIndex = Math.floor(Math.random() * totalItems);
		}

		setItemIndex(newIndex);
	};

	const resetCurrent = () => {
		initGame(); // tetap itemIndex yang sama
	};

	useEffect(() => {
		initGame();
	}, [mode, parittaIndex, itemIndex]);

  useEffect(() => {
    matchAudioRef.current = new Audio("/sounds/bell.mp3");
    winAudioRef.current = new Audio("/sounds/win-bell.mp3");

    matchAudioRef.current.volume = 0.4;
    winAudioRef.current.volume = 0.6;

    initGame();
  }, [mode]);

  // ================= SPLIT (WITH GAP) =================
  const splitText = (text) => {
    const size = mode === "easy" ? 4 : 2;
    const segments = text.split(" ");
    const result = [];

    segments.forEach((seg, idx) => {
      for (let i = 0; i < seg.length; i += size) {
        result.push({ text: seg.slice(i, i + size), type: "word" });
      }
      if (idx < segments.length - 1) {
        result.push({ type: "gap" });
      }
    });

    return result;
  };

  const initGame = () => {
    const parts = splitText(current.text);
    const words = parts.filter((p) => p.type === "word");

    setChunks(parts);
    setShuffled(shuffle([...words]));
    setAnswer([]);
    setCompleted(false);
    setIsWrong(false);
  };

  // ================= PINYIN =================
  function buildPinyin(text) {
    const chineseOnly = text.replace(/[^\u4e00-\u9fa5]/g, "");
    const raw = toPinyinSync(chineseOnly);
    return capitalizePinyin(raw);
  }

  // ================= CLICK =================
  const handleSelect = (item, idx) => {
    if (completed) return;

    const newAnswer = [...answer, item];
    setAnswer(newAnswer);

    const newShuffled = shuffled.filter((_, i) => i !== idx);
    setShuffled(newShuffled);

    // 🔔 sound
    matchAudioRef.current.currentTime = 0;
    matchAudioRef.current.play();

    const correct = chunks
      .filter((c) => c.type === "word")
      .map((c) => c.text)
      .join("");

    const currentStr = newAnswer.map((a) => a.text).join("");

    if (currentStr === correct) {
      setCompleted(true);
      setIsWrong(false);

      setTimeout(() => {
        winAudioRef.current.currentTime = 0;
        winAudioRef.current.play();
      }, 300);
      return;
    }

    if (newAnswer.length === chunks.filter((c) => c.type === "word").length) {
      setIsWrong(true);
    } else {
      setIsWrong(false);
    }
  };

  const handleRemove = (item, idx) => {
    if (completed) return;

    const newAnswer = answer.filter((_, i) => i !== idx);
    setAnswer(newAnswer);
    setShuffled([...shuffled, item]);
    setIsWrong(false);
  };

  const answerClone = [...answer];

  return (
    <MasterLayoutGames title="🧩 Paritta Puzzle">
			<div style={styles.parittaHeader}>
				{currentGroup.title}<br/>
				<span style={styles.pinyinResult}>{buildPinyin(currentGroup.title)}</span>
			</div>			

			<div style={styles.parittaSelector}>
				{data.map((g, i) => (
					<button
						key={i}
						style={i === parittaIndex ? styles.parittaActive : styles.parittaBtn}
						onClick={() => {
							setParittaIndex(i);
							setItemIndex(0);
						}}
					>
						{g.title}
					</button>
				))}
			</div>

      {/* LEVEL BUTTON */}
      <div style={styles.mode}>
				<div style={styles.modeLeft}>
					<button
						style={mode === "easy" ? styles.modeActive : styles.modeBtn}
						onClick={() => setMode("easy")}
					>
						簡單 Easy
					</button>
					<button
						style={mode === "hard" ? styles.modeActive : styles.modeBtn}
						onClick={() => setMode("hard")}
					>
						困難 Hard
					</button>
				</div>

				<div style={styles.actionRight}>
					<button style={styles.secondaryBtn} onClick={resetCurrent}>
						重排 Acak Ulang
					</button>

					<button style={styles.primaryBtn} onClick={changeRandomItem}>
						新題 Soal Baru
					</button>
				</div>
			</div>

      {/* TARGET SLOT */}
      <div style={styles.target}>
        {chunks.map((c, i) => {
          if (c.type === "gap") {
            return <div key={i} style={styles.gap}></div>;
          }

					const filled = answerClone.shift();

					return (
						<div
							key={i}
							style={{
								...styles.targetBox,
								minWidth: mode === "easy" ? "10rem" : "6rem",
								...(filled ? styles.snapIn : {}),
							}}
						>
							{filled ? (
								<div style={styles.tileInner}>
									<div style={styles.hanzi}>{filled.text}</div>
									<div style={styles.pinyinSmall}>
										{filled.pinyin || buildPinyin(filled.text)}
									</div>
								</div>
							) : null}
						</div>
					);
        })}
      </div>

      {/* ERROR */}
      {isWrong && !completed && (
        <div style={styles.errorBox}>
          <div>Susunan belum tepat. Periksa kembali urutan kalimat.</div>
        </div>
      )}

      {/* OPTIONS */}
      <div style={styles.grid}>
        {shuffled.map((c, i) => (
          <div
            key={i}
            style={styles.tile}
            onClick={() => handleSelect(c, i)}
          >
            <div>{c.text}</div>
            <div style={styles.pinyinSmall}>
              {buildPinyin(c.text)}
            </div>
          </div>
        ))}
      </div>

      {/* ANSWER */}
      <div style={styles.answer}>
        {answer.map((c, i) => (
          <div
            key={i}
            style={styles.tileSelected}
            onClick={() => handleRemove(c, i)}
          >
            <div>{c.text}</div>
            <div style={styles.pinyinSmall}>
              {buildPinyin(c.text)}
            </div>
          </div>
        ))}
      </div>

      {/* RESULT */}
			{completed && (
				<div style={styles.result}>
          <div style={{marginBottom:"0.5rem"}}>Jawaban Anda tepat!</div>

					<div style={styles.full}>{current.text}</div>
					<div style={styles.pinyinResult}>{buildPinyin(current.text)}</div>
					<div style={styles.translation}>{current.translation}</div>

          <div style={styles.pinyinNote}>
            Sutra/Paritta mengandung makna yang mendalam. Terjemahan memiliki banyak keterbatasan, sehingga pemahaman dapat berbeda. Dengan sering membaca dan mempelajari, penghayatan serta pemahaman akan semakin berkembang.
          </div>
				</div>
			)}

      {/* DISCLAIMER */}
      <div style={styles.pinyinNote}>
        <br/>
        Pinyin dihasilkan secara otomatis oleh sistem. Satu aksara Mandarin
        dapat memiliki beberapa kemungkinan pelafalan tergantung konteks.
      </div>

    </MasterLayoutGames>
  );
}

// ================= UTILS =================
function shuffle(arr) {
  return arr.sort(() => Math.random() - 0.5);
}

// ================= STYLES =================
const styles = {
  mode: {
    display: "flex",
    alignItems: "center",
    marginBottom: "1rem",
  },
  modeBtn: {
    fontSize: "1.1rem",
    padding: "0.6rem 1.2rem",
    borderRadius: "1.2rem",
    border: "none",
    background: "#ddd",
    cursor: "pointer",
  },
  modeActive: {
    fontSize: "1.1rem",
    padding: "0.6rem 1.2rem",
    borderRadius: "1.2rem",
    border: "none",
    background: "#7cc6a6",
    color: "#fff",
    cursor: "pointer",
  },

  target: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.4rem",
    marginBottom: "1rem",
  },
  gap: {
    width: "100%",
    height: "0.8rem",
  },
  targetBox: {
    padding: "5px 20px",
    minWidth: "6rem",
    height: "2.8rem",
    borderRadius: "0.7rem",
    background: "#d6d0c4",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "600",
  },

  // ✨ SNAP ANIMATION
  snapIn: {
    animation: "snap 0.25s ease",
  },

  grid: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.5rem",
    marginBottom: "1rem",
  },
	tile: {
		padding: "0.6rem 0.9rem",
		borderRadius: "1rem",
		background: "#ffffff",
		cursor: "pointer",
		textAlign: "center",

		display: "flex",
		flexDirection: "column",   // 🔥 penting
		alignItems: "center",
		justifyContent: "center",
	},
  answer: {
    display: "none",
  },
	tileSelected: {
		padding: "0.6rem 0.9rem",
		borderRadius: "1rem",
		background: "#cdeac0",
		cursor: "pointer",
		textAlign: "center",

		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},

	tileInner: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},

	hanzi: {
		fontSize: "1.1rem",
		fontWeight: "600",
		lineHeight: "1.2",
	},

  pinyinSmall: {
    fontSize: "0.7rem",
    color: "#888",
    marginTop: "0.2rem",
		lineHeight: "1.1",
  },

  errorBox: {
    background: "#ffe5e5",
    padding: "0.8rem",
    borderRadius: "0.8rem",
    marginBottom: "1rem",
    display: "flex",
    justifyContent: "space-between",
  },

	modeLeft: {
		display: "flex",
		gap: "0.6rem",
	},

	resetBtnRight: {
		marginLeft: "auto", // 🔥 dorong ke kanan
		padding: "0.5rem 1rem",
		borderRadius: "0.8rem",
		border: "none",
		background: "#d9534f",
		color: "#fff",
		cursor: "pointer",
	},

  pinyinNote: {
    fontSize: "0.8rem",
    color: "#888",
    marginTop: "1rem",
  },

	pinyinResult: {
		fontSize: "0.95rem",
		color: "#666",
		marginBottom: "0.6rem",
	},
  result: {
    marginTop: "1.5rem",
    padding: "1rem",
    borderRadius: "1rem",
    background: "#f8f9fa",
  },
  full: {
    fontSize: "1.2rem",
    marginBottom: "0rem",
  },
  translation: {
    fontSize: "1rem",
  },

	parittaHeader: {
		fontSize: "1.3rem",
		fontWeight: "600",
		marginBottom: "0.6rem",
	},

	parittaSelector: {
		display: "flex",
		gap: "0.5rem",
		overflowX: "auto",
		marginBottom: "1rem",
	},

	parittaBtn: {
		padding: "0.4rem 0.9rem",
		borderRadius: "1rem",
		border: "none",
		background: "rgb(232, 233, 166)",
		cursor: "pointer",
		whiteSpace: "nowrap",
	},

	parittaActive: {
		padding: "0.4rem 0.9rem",
		borderRadius: "1rem",
		border: "none",
		background: "#dcc005",
		color: "#fff",
		cursor: "pointer",
		whiteSpace: "nowrap",
	},

	actionRight: {
		marginLeft: "auto",
		display: "flex",
		gap: "0.5rem",
	},

	secondaryBtn: {
		padding: "0.5rem 0.9rem",
		borderRadius: "0.8rem",
		border: "none",
		background: "#d9534f",
		color: "#fff",
		cursor: "pointer",
	},

	primaryBtn: {
		padding: "0.5rem 1rem",
		borderRadius: "0.8rem",
		border: "none",
		background: "#1c128f",
		color: "#fff",
		cursor: "pointer",
	},
};